import React, { useState, useEffect, useRef } from "react";
import {
  SyncOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { theme } from "antd";

/**
 * Resumen de los registros que Business Central rechazó durante la última
 * sincronización.
 *
 * Existe porque el indicador de una línea no servía para esto: cada paso de la
 * sincronización pisa el mensaje del anterior, y en el bloque de catálogos, que
 * corre en paralelo, un motivo de rechazo duraba milisegundos. Este panel se
 * arma una sola vez al terminar la corrida y se queda hasta que lo cierren o
 * hasta que empiece una sincronización nueva. Ver SANTPV-59.
 */
const SyncFailuresPanel = ({ failures, onClose }) => {
  const { token } = theme.useToken();

  return (
    <div
      role="alert"
      style={{
        backgroundColor: token.colorBgElevated,
        color: token.colorText,
        borderLeft: "4px solid #faad14",
        borderRadius: "8px",
        padding: "12px 14px",
        fontSize: "13px",
        maxWidth: "420px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        animation: "slideIn 0.3s ease-out",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <ExclamationCircleOutlined
          style={{ color: "#faad14", fontSize: "16px", marginTop: "2px" }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: "6px" }}>
            Business Central no aplicó algunos cambios
          </div>
          <ul style={{ margin: 0, paddingLeft: "18px", lineHeight: "1.5" }}>
            {failures.map((failure, index) => (
              <li key={`${failure.operationName}-${index}`} style={{ marginBottom: "4px" }}>
                <strong>{failure.operationName}:</strong>{" "}
                <span style={{ wordBreak: "break-word" }}>{failure.message}</span>
              </li>
            ))}
          </ul>
          <div style={{ marginTop: "8px", color: "#8c8c8c" }}>
            Los registros afectados siguen en su listado, marcados con el motivo.
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar el resumen"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#8c8c8c",
            padding: "2px",
            lineHeight: 1,
          }}
        >
          <CloseOutlined style={{ fontSize: "12px" }} />
        </button>
      </div>
    </div>
  );
};

const SyncStatusIndicator = () => {
  const [syncStatus, setSyncStatus] = useState({
    isVisible: false,
    message: "",
    isComplete: false,
  });
  // Los fallos parciales no comparten estado con el indicador: éste se va solo a
  // los 5 segundos y aquéllos tienen que quedarse hasta que alguien los lea.
  const [failures, setFailures] = useState([]);
  const hideTimeout = useRef(null);

  useEffect(() => {
    const handleSyncStatus = (event) => {
      const { message, isComplete } = event.detail;

      // Cancelar timeout anterior si existe
      if (hideTimeout.current) {
        clearTimeout(hideTimeout.current);
        hideTimeout.current = null;
      }

      // Detectar si es error o cancelación
      const isError = message.toLowerCase().includes("error");
      const isCanceled = message.toLowerCase().includes("cancelada");

      // Si es evento de cierre (completado, error o cancelación)
      if (isComplete || isError || isCanceled) {
        setSyncStatus({
          isVisible: true,
          message,
          isComplete: true,
        });

        hideTimeout.current = setTimeout(() => {
          setSyncStatus({ isVisible: false, message: "", isComplete: false });
          hideTimeout.current = null;
        }, 5000);

        return;
      }

      // Caso normal: sincronización en curso
      setSyncStatus({
        isVisible: true,
        message,
        isComplete: false,
      });
    };

    // Una lista vacía es la señal de que arrancó una corrida nueva: el resumen
    // de la anterior deja de tener sentido y se limpia.
    const handleSyncFailures = (event) => {
      setFailures(event.detail?.failures || []);
    };

    window.addEventListener("syncStatus", handleSyncStatus);
    window.addEventListener("syncFailures", handleSyncFailures);
    return () => {
      window.removeEventListener("syncStatus", handleSyncStatus);
      window.removeEventListener("syncFailures", handleSyncFailures);
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, []);

  if (!syncStatus.isVisible && failures.length === 0) return null;

  // Clasificación visual
  const isError = syncStatus.message.toLowerCase().includes("error");
  const isCanceled = syncStatus.message.toLowerCase().includes("cancelada");
  const isRetrying = syncStatus.message.toLowerCase().includes("reintentando");
  const isCompleted = syncStatus.isComplete && !isError && !isCanceled;

  const getBackgroundColor = () => {
    if (isError) return "#ff4d4f";
    if (isCanceled) return "#d46b08";
    if (isCompleted) return "#52c41a";
    if (isRetrying) return "#faad14";
    return "#1677ff";
  };

  const getIcon = () => {
    if (isError) return <ExclamationCircleOutlined style={{ fontSize: "14px" }} />;
    if (isCanceled) return <StopOutlined style={{ fontSize: "14px" }} />;
    if (isCompleted) return <CheckCircleOutlined style={{ fontSize: "14px" }} />;
    return <SyncOutlined spin style={{ fontSize: "14px" }} />;
  };

  return (
    // Los dos se apilan en una columna en vez de posicionarse por separado: si
    // no, el resumen y el indicador quedan uno encima del otro.
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        left: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "10px",
        zIndex: 1001,
      }}
    >
      {failures.length > 0 && (
        <SyncFailuresPanel failures={failures} onClose={() => setFailures([])} />
      )}

      {syncStatus.isVisible && (
        <div
          style={{
            backgroundColor: getBackgroundColor(),
            color: "white",
            padding: "10px 16px",
            borderRadius: "8px",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            maxWidth: "400px",
            minWidth: "200px",
            wordBreak: "break-word",
            animation: "slideIn 0.3s ease-out",
            border: isError ? "1px solid #d9534f" : "none",
          }}
        >
          {getIcon()}
          <span style={{ lineHeight: "1.4" }}>{syncStatus.message}</span>
        </div>
      )}
    </div>
  );
};

export default SyncStatusIndicator;
